import React, { memo, useId } from 'react'

import {
  Button, Icon, Refresh, BrushAI, CubeAIOutline, classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, shape, string } from 'prop-types'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import DownloadButton from 'Pods/ThreeDStudio/Shared/DownloadButton/DownloadButton'

const applyIcon = <Icon svgComponent={CubeAIOutline} size={iconSize} notSpaced />
const regenerateIcon = <Icon svgComponent={Refresh} size={iconSize} notSpaced />
const editIcon = <Icon svgComponent={BrushAI} size={iconSize} notSpaced />

// Apply / regenerate / edit / download for one generated image. Rendered both inline
// under the conversation result and inside the lightbox, so the two cannot drift.
function ImageActions({ result, isGenerating, className = '', onApply, onRegenerate, onEdit }) {
  const t = useTranslation()
  // SVG strokes and fills cannot take a CSS gradient, so the AI glyph paints from a real
  // <linearGradient>. React's useId emits colons, which are awkward inside url(#...).
  const gradientId = `image-actions-ai-gradient-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`

  return (
    <div
      className={classnames('image-assistant-panel__actions', className)}
      style={{ '--image-actions-ai-icon-paint': `url(#${gradientId})` }}
    >
      <svg className="image-assistant-panel__gradient-defs" width="0" height="0" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="24" y2="0" gradientUnits="userSpaceOnUse">
            <stop className="image-assistant-panel__gradient-stop--start" offset="0" />
            <stop className="image-assistant-panel__gradient-stop--end" offset="1" />
          </linearGradient>
        </defs>
      </svg>
      <Button
        type="secondary"
        size="small"
        variant="with-icon"
        customClass="image-assistant-panel__action image-assistant-panel__action--ai"
        icon={applyIcon}
        onClick={onApply}
      >
        <span className="image-assistant-panel__action-label cgt-body-base-semibold">
          {t('imageAssistantPanel.actions.useForThreeDGeneration')}
        </span>
      </Button>
      <Button
        type="secondary"
        size="small"
        variant="with-icon"
        customClass="image-assistant-panel__action"
        icon={regenerateIcon}
        disabled={isGenerating}
        onClick={onRegenerate}
      >
        {t('imageAssistantPanel.actions.regenerate')}
      </Button>
      <Button
        type="secondary"
        size="small"
        variant="with-icon"
        customClass="image-assistant-panel__action"
        icon={editIcon}
        onClick={onEdit}
      >
        {t('imageAssistantPanel.actions.editImage')}
      </Button>
      <DownloadButton result={result} />
    </div>
  )
}

ImageActions.propTypes = {
  result: shape({ id: number, url: string, alt: string }).isRequired,
  isGenerating: bool.isRequired,
  className: string,
  onApply: func.isRequired,
  onRegenerate: func.isRequired,
  onEdit: func.isRequired,
}

export default memo(ImageActions)
